import { DomainError, TransportError, } from "@econ-v1/domain";
import { isJsonValue } from "./errors.js";
import { deserializeRpcEvent, deserializeRpcResponse, serializeRpcRequest, } from "./protocol.js";
/**
 * A single kernel RPC request exceeded its local response deadline. This is deliberately
 * distinct from connection loss: the transport remains usable and callers may retry.
 */
export class KernelRpcTimeoutError extends Error {
    commandName;
    correlationId;
    timeoutMs;
    constructor(commandName, correlationId, timeoutMs) {
        super(`Kernel RPC "${commandName}" (${correlationId}) timed out after ${timeoutMs}ms; the request may be retried`);
        this.name = "KernelRpcTimeoutError";
        this.commandName = commandName;
        this.correlationId = correlationId;
        this.timeoutMs = timeoutMs;
    }
}
// Kernel RPC is local, but stage work can cross browser/worker scheduling boundaries.
// Thirty seconds bounds dropped fire-and-forget messages without penalizing slow devices.
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
export class MessagePortKernelRpcClient {
    #eventGroupsByKey = new Map();
    #eventGroupsBySubscription = new Map();
    #liveGroupsByKey = new Map();
    #liveGroupsBySubscription = new Map();
    #now;
    #pending = new Map();
    #requestTimeoutMs;
    #transport;
    #unsubscribeClose;
    #unsubscribeMessages;
    #closed = false;
    #nextRequestId = 1;
    #nextSubscriptionId = 1;
    constructor(transport, options = {}) {
        this.#transport = transport;
        this.#now = options.now ?? (() => new Date().toISOString());
        this.#requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
        this.#unsubscribeMessages = transport.subscribe((message) => this.#handleMessage(message));
        this.#unsubscribeClose = transport.subscribeClose(() => this.#handleClose());
    }
    close() {
        if (this.#closed)
            return;
        this.#transport.close();
        this.#handleClose();
    }
    async command(name, payload) {
        return await this.#request({ name, payload: jsonValue(payload, "command payload"), type: "command" });
    }
    liveQuery(name, params, listener) {
        return this.#subscribeLiveQuery(undefined, name, jsonValue(params, "live-query params"), listener);
    }
    async openStageSession(appName, manifestSha256) {
        const value = await this.#request({ appName, manifestSha256, type: "stage.open-session" });
        if (!isRecord(value) || typeof value.sessionId !== "string") {
            throw new DomainError({ code: "invalid_request", message: "Invalid stage session response" });
        }
        return new BoundStageRpcSession(this, value.sessionId);
    }
    async prepareStage(appName) {
        const value = await this.#request({ appName, type: "stage.prepare" });
        if (!isRecord(value) ||
            typeof value.appName !== "string" ||
            typeof value.entryUrl !== "string" ||
            typeof value.manifestSha256 !== "string" ||
            typeof value.version !== "string") {
            throw new DomainError({ code: "invalid_request", message: "Invalid prepared stage response" });
        }
        return {
            appName: value.appName,
            entryUrl: value.entryUrl,
            manifestSha256: value.manifestSha256,
            version: value.version,
        };
    }
    async query(name, params) {
        return await this.#request({ name, params: jsonValue(params, "query params"), type: "query" });
    }
    subscribe(type, listener) {
        return this.#subscribeEvent(undefined, type, listener);
    }
    closeStageSession(sessionId) {
        const error = new TransportError({ code: "connection_lost", message: "Stage RPC session closed" });
        for (const [requestId, pending] of [...this.#pending]) {
            if (pending.sessionId === sessionId) {
                this.#takePending(requestId)?.reject(error);
            }
        }
        this.#removeSessionSubscriptions(sessionId);
        void this.#request({ sessionId, type: "stage.close-session" }).catch(() => undefined);
    }
    invokeStageCapability(sessionId, capability, payload) {
        return this.#request({
            capability,
            payload: jsonValue(payload, "capability payload"),
            sessionId,
            type: "stage.capability.invoke",
        });
    }
    liveQueryStage(sessionId, name, params, listener) {
        return this.#subscribeLiveQuery(sessionId, name, jsonValue(params, "live-query params"), listener);
    }
    publishStage(sessionId, type, payload) {
        const event = jsonValue({ occurredAt: this.#now(), payload, type }, "domain event");
        return this.#request({ event, sessionId, type: "stage.event.publish" }).then(() => undefined);
    }
    queryStage(sessionId, name, params) {
        return this.#request({
            name,
            params: jsonValue(params, "query params"),
            sessionId,
            type: "stage.query",
        });
    }
    subscribeStage(sessionId, type, listener) {
        return this.#subscribeEvent(sessionId, type, listener);
    }
    #handleClose() {
        if (this.#closed)
            return;
        this.#closed = true;
        this.#unsubscribeMessages();
        this.#unsubscribeClose();
        const error = new TransportError({ code: "connection_lost", message: "Kernel RPC port closed" });
        for (const requestId of [...this.#pending.keys()])
            this.#takePending(requestId)?.reject(error);
        this.#eventGroupsByKey.clear();
        this.#eventGroupsBySubscription.clear();
        this.#liveGroupsByKey.clear();
        this.#liveGroupsBySubscription.clear();
    }
    #handleMessage(message) {
        let envelope;
        try {
            envelope = JSON.parse(message);
        }
        catch {
            return;
        }
        if (isRecord(envelope) && "id" in envelope) {
            const responseId = typeof envelope.id === "string" ? envelope.id : undefined;
            try {
                const response = deserializeRpcResponse(message);
                const pending = this.#takePending(response.id);
                if (pending === undefined)
                    return;
                if (response.ok)
                    pending.resolve(response.value);
                else
                    pending.reject(response.error);
                return;
            }
            catch {
                if (responseId !== undefined) {
                    const pending = this.#takePending(responseId);
                    if (pending !== undefined) {
                        pending.reject(new DomainError({ code: "invalid_request", message: "Malformed kernel RPC response" }));
                    }
                }
                return;
            }
        }
        try {
            const event = deserializeRpcEvent(message);
            if (event.type === "kernel.event") {
                const group = this.#eventGroupsBySubscription.get(event.subscriptionId);
                if (group === undefined || group.eventType !== event.event.type)
                    return;
                for (const listener of [...group.listeners])
                    this.#notify(() => listener(event.event));
                return;
            }
            const group = this.#liveGroupsBySubscription.get(event.subscriptionId);
            if (group === undefined)
                return;
            for (const listener of [...group.listeners])
                this.#notify(() => listener(event.diff));
        }
        catch {
            // Malformed or stale port messages are isolated from valid pending requests.
        }
    }
    #notify(listener) {
        try {
            listener();
        }
        catch {
            // One UI listener must not suppress later listeners on the same RPC subscription.
        }
    }
    #takePending(requestId) {
        const pending = this.#pending.get(requestId);
        if (pending === undefined)
            return undefined;
        this.#pending.delete(requestId);
        globalThis.clearTimeout(pending.timeoutId);
        return pending;
    }
    #removeSessionSubscriptions(sessionId) {
        for (const [key, group] of [...this.#eventGroupsByKey]) {
            if (group.sessionId === sessionId) {
                this.#eventGroupsByKey.delete(key);
                this.#eventGroupsBySubscription.delete(group.subscriptionId);
            }
        }
        for (const [key, group] of [...this.#liveGroupsByKey]) {
            if (group.sessionId === sessionId) {
                this.#liveGroupsByKey.delete(key);
                this.#liveGroupsBySubscription.delete(group.subscriptionId);
            }
        }
    }
    #request(request) {
        if (this.#closed || this.#transport.state !== "open") {
            return Promise.reject(new TransportError({ code: "connection_lost", message: "Kernel RPC port is closed" }));
        }
        const id = `request-${this.#nextRequestId}`;
        this.#nextRequestId += 1;
        return new Promise((resolve, reject) => {
            const sessionId = "sessionId" in request ? request.sessionId : undefined;
            const commandName = "name" in request
                ? request.name
                : "capability" in request ? request.capability : request.type;
            const timeoutId = globalThis.setTimeout(() => {
                this.#takePending(id)?.reject(new KernelRpcTimeoutError(commandName, id, this.#requestTimeoutMs));
            }, this.#requestTimeoutMs);
            this.#pending.set(id, { reject, resolve, sessionId, timeoutId });
            try {
                this.#transport.send(serializeRpcRequest({ ...request, id }));
            }
            catch (error) {
                const sendError = error instanceof TransportError
                    ? error
                    : new TransportError({ code: "connection_lost", message: "Kernel RPC send failed" });
                this.#takePending(id)?.reject(sendError);
            }
        });
    }
    #subscribeEvent(sessionId, eventType, listener) {
        const key = `${sessionId ?? "host"}:event:${eventType}`;
        let group = this.#eventGroupsByKey.get(key);
        if (group === undefined) {
            const subscriptionId = this.#newSubscriptionId();
            group = { eventType, listeners: new Set(), sessionId, subscriptionId };
            this.#eventGroupsByKey.set(key, group);
            this.#eventGroupsBySubscription.set(subscriptionId, group);
            const request = sessionId === undefined
                ? { eventType, subscriptionId, type: "event.subscribe" }
                : { eventType, sessionId, subscriptionId, type: "stage.event.subscribe" };
            void this.#request(request).catch(() => this.#dropEventGroup(key, group));
        }
        const storedListener = listener;
        group.listeners.add(storedListener);
        let active = true;
        return () => {
            if (!active)
                return;
            active = false;
            group?.listeners.delete(storedListener);
            if (group !== undefined && group.listeners.size === 0) {
                this.#dropEventGroup(key, group);
                const request = sessionId === undefined
                    ? { subscriptionId: group.subscriptionId, type: "event.unsubscribe" }
                    : { sessionId, subscriptionId: group.subscriptionId, type: "stage.event.unsubscribe" };
                void this.#request(request).catch(() => undefined);
            }
        };
    }
    #subscribeLiveQuery(sessionId, name, params, listener) {
        const key = `${sessionId ?? "host"}:live:${name}:${JSON.stringify(params)}`;
        let group = this.#liveGroupsByKey.get(key);
        if (group === undefined) {
            const subscriptionId = this.#newSubscriptionId();
            group = { listeners: new Set(), name, params, sessionId, subscriptionId };
            this.#liveGroupsByKey.set(key, group);
            this.#liveGroupsBySubscription.set(subscriptionId, group);
            const request = sessionId === undefined
                ? { name, params, subscriptionId, type: "live-query.subscribe" }
                : { name, params, sessionId, subscriptionId, type: "stage.live-query.subscribe" };
            void this.#request(request).catch(() => this.#dropLiveGroup(key, group));
        }
        const storedListener = listener;
        group.listeners.add(storedListener);
        let active = true;
        return () => {
            if (!active)
                return;
            active = false;
            group?.listeners.delete(storedListener);
            if (group !== undefined && group.listeners.size === 0) {
                this.#dropLiveGroup(key, group);
                const request = sessionId === undefined
                    ? { subscriptionId: group.subscriptionId, type: "live-query.unsubscribe" }
                    : { sessionId, subscriptionId: group.subscriptionId, type: "stage.live-query.unsubscribe" };
                void this.#request(request).catch(() => undefined);
            }
        };
    }
    #dropEventGroup(key, group) {
        if (this.#eventGroupsByKey.get(key) === group)
            this.#eventGroupsByKey.delete(key);
        this.#eventGroupsBySubscription.delete(group.subscriptionId);
    }
    #dropLiveGroup(key, group) {
        if (this.#liveGroupsByKey.get(key) === group)
            this.#liveGroupsByKey.delete(key);
        this.#liveGroupsBySubscription.delete(group.subscriptionId);
    }
    #newSubscriptionId() {
        const id = `subscription-${this.#nextSubscriptionId}`;
        this.#nextSubscriptionId += 1;
        return id;
    }
}
class BoundStageRpcSession {
    #client;
    #sessionId;
    #closed = false;
    constructor(client, sessionId) {
        this.#client = client;
        this.#sessionId = sessionId;
    }
    close() {
        if (this.#closed)
            return;
        this.#closed = true;
        this.#client.closeStageSession(this.#sessionId);
    }
    invokeCapability(capability, payload) {
        this.#assertOpen();
        return this.#client.invokeStageCapability(this.#sessionId, capability, payload);
    }
    liveQuery(name, params, listener) {
        this.#assertOpen();
        return this.#client.liveQueryStage(this.#sessionId, name, params, listener);
    }
    publish(type, payload) {
        this.#assertOpen();
        return this.#client.publishStage(this.#sessionId, type, payload);
    }
    query(name, params) {
        this.#assertOpen();
        return this.#client.queryStage(this.#sessionId, name, params);
    }
    subscribe(type, listener) {
        this.#assertOpen();
        return this.#client.subscribeStage(this.#sessionId, type, listener);
    }
    #assertOpen() {
        if (this.#closed)
            throw new TransportError({ code: "connection_lost", message: "Stage RPC session is closed" });
    }
}
function jsonValue(value, label) {
    if (isJsonValue(value))
        return value;
    throw new DomainError({ code: "validation_failed", message: `${label} must be JSON-serializable` });
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=message-port-client.js.map